/**
 * @graphql cc
 */
import React, { useMemo, useRef } from 'react';
import { graphql, RelayEnvironmentProvider } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/provider';
import type { CardStore } from '@atlaskit/linking-common/store';
import { functionUnionWithCondition } from '@atlaskit/platform-feature-flags-react/function-union-with-condition';

import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel/utils/custom-domains';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { fg } from '@confluence/feature-gating';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { getLogger } from '@confluence/logger';
import { usePageSpaceKey } from '@confluence/page-context';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PreviewPanelsPostMessageListener } from '@confluence/preview-panel-api/entry-points/PreviewPanelsPostMessageListener.ts';
import { useOpenPreviewPanel } from '@confluence/preview-panel-api/entry-points/useOpenPreviewPanel.ts';
import { isPreviewPanelAvailable } from '@confluence/preview-panel-api/entry-points/isPreviewPanelAvailable.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	type FragmentReaderInterop,
	useReadFragmentInterop,
} from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import {
	useWriteFragmentInterop,
	type FragmentWriterInterop,
} from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment.graphql.ts';
import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql.ts';
import ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment_artifact from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql.ts';
import { ConfluenceSmartCardClient } from './ConfluenceSmartCardClient.ts';
import { liveDocSmartCardResolver } from './resolvers/liveDocSmartCardResolver.ts';

export type ConfluenceSmartCardProviderProps = Parameters<typeof SmartCardProvider>[0] & {
	doNotUsePreviewPanels?: boolean;
};

const MaybeConditionalRelayEnvironmentProvider = ({ children }: { children: React.ReactNode }) =>
	fg('ccp-9584-remove-relay-environment-provider') ? (
		<RelayEnvironmentProvider environment={getRelayEnvironment()}>
			{children}
		</RelayEnvironmentProvider>
	) : (
		<ConditionalRelayEnvironmentProvider>{children}</ConditionalRelayEnvironmentProvider>
	);

// Only log first error to console, to prevent spamming console with resolver errors
let hasLoggedResolverError = false;
const defaultConfluenceResolveHandler = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	readFragmentInterop: FragmentReaderInterop<any>,
	writeFragmentInterop: FragmentWriterInterop<any>,
	data?: unknown,
) => {
	try {
		// NOTE FOR DEVS OF RESOLVERS: The `data` object comes from linking platform's content resolver service which is an UNSTABLE API SCHEMA.
		// Ensure your resolver logic handles invalid schemas gracefully and is able to notify your team if the schema changes and your code needs to be updated.
		liveDocSmartCardResolver(createAnalyticsEvent, readFragmentInterop, writeFragmentInterop, data);
	} catch (resolverError) {
		// prevent errors from causing smart card provider issues
		if (!hasLoggedResolverError) {
			const logger = getLogger('ConfluenceSmartCardProvider');
			logger.error`resolver error: ${resolverError}`;
			hasLoggedResolverError = true;
		}
	}
};

const computeInitialStateFromCache = (smartCardClient: CardClient): CardStore | null => {
	// On page transition create the initial CardStore from Cache
	return !isPageSSRd() && smartCardClient instanceof ConfluenceSmartCardClient
		? smartCardClient.getInitialCardStoreFromCache()
		: null;
};

const ConsolidatedSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data>(
			{
				fragmentArtifact:
					ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment_artifact,
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[props?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	const initialStateFromCache = useRef<CardStore | null>(
		computeInitialStateFromCache(smartCardClient),
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const { children, ...restProps } = props;

	const isPageSSR = isPageSSRd();
	const initialState = initialStateFromCache?.current ?? window['__SSR_SMARTLINKS__'];
	const internalStoreOptions = useStoreOptions(initialState, isPageSSR);

	// Merge smartlink SSR data with any passed-in storeOptions (e.g. from shortcuts preloader)
	const presetInitialState = {
		...internalStoreOptions,
		...(props.storeOptions
			? {
					storeOptions: {
						...props.storeOptions,
						...(internalStoreOptions.storeOptions?.initialState
							? {
									initialState: {
										...internalStoreOptions.storeOptions.initialState,
										...props.storeOptions.initialState,
									},
								}
							: {}),
					},
				}
			: {}),
	};

	const shouldControlDataExport = useIsOnRoute(PDF_EXPORT_PAGE);

	return (
		<SmartCardProvider
			product="CONFLUENCE"
			client={smartCardClient}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			shouldControlDataExport={shouldControlDataExport}
			isPreviewPanelAvailable={restProps.isPreviewPanelAvailable}
			openPreviewPanel={restProps.openPreviewPanel}
			{...restProps}
			// props.storeOptions can be set explicitly to `undefined` -- overwriting the presetInitialState storeOptions merge with props
			{...presetInitialState}
		>
			{children}
		</SmartCardProvider>
	);
};

const PreviewPanelSmartCardProvider = ({
	children,
	...restProps
}: ConfluenceSmartCardProviderProps) => {
	const { open } = useOpenPreviewPanel();
	const { cloudId } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// Create a wrapper function that updates the preview panel URL
	// This is used for custom domains to update to the correct content URL and avoid CSP issues
	const openPreviewPanelWithModifiedURL = useMemo(() => {
		if (!cloudId) {
			return open;
		}
		return ({ url, ari, ...rest }: Parameters<typeof open>[0]) =>
			open({ url: getCorrectContentURL(cloudId, url, ari), ari, ...rest });
	}, [open, cloudId]);

	return (
		<ConsolidatedSmartCardProvider
			openPreviewPanel={openPreviewPanelWithModifiedURL}
			isPreviewPanelAvailable={renderServerPlaceholder ? (_ari) => false : isPreviewPanelAvailable}
			{...restProps}
		>
			{children}
			<PreviewPanelsPostMessageListener />
		</ConsolidatedSmartCardProvider>
	);
};

const ConsolidatedPreviewPanelSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	const [spaceKey] = usePageSpaceKey();
	const { children, doNotUsePreviewPanels, ...restProps } = props;

	return isCompanyHubSpaceKey(spaceKey) || doNotUsePreviewPanels ? (
		<ConsolidatedSmartCardProvider {...restProps}>{children}</ConsolidatedSmartCardProvider>
	) : (
		<MaybeConditionalRelayEnvironmentProvider>
			<PreviewPanelSmartCardProvider {...restProps}>{children}</PreviewPanelSmartCardProvider>
		</MaybeConditionalRelayEnvironmentProvider>
	);
};

export const ConfluenceSmartCardProvider = (
	props: ConfluenceSmartCardProviderProps,
): React.JSX.Element => {
	let updatedProps: ConfluenceSmartCardProviderProps = props;

	const useSessionDataGated: () => any = functionUnionWithCondition(
		() => fg('platform_sl_3p_auth_rovo_entitlement'),
		useSessionData,
		() => undefined,
	);

	const sessionData = useSessionDataGated();
	updatedProps = useMemo(
		() => ({
			...props,
			...((!!sessionData && fg('platform_sl_3p_auth_rovo_entitlement')
				? {
						rovoOptions: {
							isRovoEnabled: sessionData?.isRovoEnabled ?? false,
							isRovoLLMEnabled: sessionData?.isRovoLLMEnabled ?? false,
						},
					}
				: undefined) || {}),
		}),
		[props, sessionData],
	);

	return <ConsolidatedPreviewPanelSmartCardProvider {...updatedProps} />;
};
